// Frontend derivation for the Volume page — turns the raw API payload into the view model the
// widgets render. ALL tile/chart/table maths live here so the page is
// independent of where the payload came from.

// Display-window options. The payload always carries 26 months; the filter
// selects how many recent months to SHOW. A custom range picks an explicit
// from/to. YoY for a shown month reads the row 12 months earlier in the payload.
const PERIODS = {
  "3m": { label: "Last 3 months", months: 3 },
  "6m": { label: "Last 6 months", months: 6 },
  "12m": { label: "Last 12 months", months: 12 },
  "24m": { label: "Last 24 months", months: 24 },
}

export const DEFAULT_PERIOD = "3m"
export const PERIOD_OPTIONS = Object.entries(PERIODS).map(([key, p]) => ({ key, label: p.label }))

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// "2026-03" -> "Mar 26"
const fmtYM = ym => {
  const [y, m] = ym.split("-").map(Number)
  return `${MONTHS_SHORT[m - 1]} ${String(y).slice(2)}`
}

// "YYYY-MM" minus 12 months.
const priorYm = ym => {
  const [y, m] = ym.split("-").map(Number)
  return `${y - 1}-${String(m).padStart(2, "0")}`
}

const subtractMonths = (ym, months) => {
  const [year, month] = ym.split("-").map(Number)
  const date = new Date(year, month - 1 - months, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
}

const yoyPct = (curr, prior) => (prior && prior > 0 ? Math.round(((curr - prior) / prior) * 1000) / 10 : null)
const sum = (arr, f) => arr.reduce((s, r) => s + (f(r) || 0), 0)
const avgPerOrder = (units, orders) => (orders ? +(units / orders).toFixed(1) : 0)

// `period` is either a PERIODS key string, or { custom: true, fromYM, toYM }.
export const deriveVolumeView = (payload, period) => {
  const { info, outbound = [], inbound = [], dwh_last_date, dwh_unavailable } = payload || {}
  const currentYM = new Date().toISOString().slice(0, 7)
  const name = info?.hs_name || info?.xero_name || info?.wms_name || info?.company_code || "Your account"

  // Resolve the display window.
  let obWindow
  let ibWindow
  let periodKey
  let periodLabel
  if (period && period.custom && period.fromYM && period.toYM) {
    obWindow = outbound.filter(r => r.year_month >= period.fromYM && r.year_month <= period.toYM)
    ibWindow = inbound.filter(r => r.year_month >= period.fromYM && r.year_month <= period.toYM)
    periodKey = "custom"
    periodLabel = `${fmtYM(period.fromYM)} – ${fmtYM(period.toYM)}`
  } else {
    periodKey = period in PERIODS ? period : DEFAULT_PERIOD
    const fromYM = subtractMonths(currentYM, PERIODS[periodKey].months - 1)
    obWindow = outbound.filter(r => r.year_month >= fromYM && r.year_month <= currentYM)
    ibWindow = inbound.filter(r => r.year_month >= fromYM && r.year_month <= currentYM)
    periodLabel = PERIODS[periodKey].label
  }

  const months = (obWindow.length ? obWindow : ibWindow).map(r => r.year_month)
  const monthSet = new Set(months)
  const partialIndex = months.indexOf(currentYM)
  const labels = months.map(fmtYM)

  const ibByM = Object.fromEntries(inbound.map(r => [r.year_month, r]))
  const obByM = Object.fromEntries(outbound.map(r => [r.year_month, r]))

  // Tiles use COMPLETE months only (exclude the partial current month).
  const obComplete = obWindow.filter(r => r.year_month < currentYM)
  const ibComplete = ibWindow.filter(r => monthSet.has(r.year_month) && r.year_month < currentYM)

  // ---- Outbound: per-channel KPIs (created + despatched) ----
  const channel = (createdO, createdU, desO, desU) => {
    const co = sum(obComplete, createdO)
    const cu = sum(obComplete, createdU)
    const dobs = sum(obComplete, desO)
    const dus = sum(obComplete, desU)
    return {
      createdOrders: co,
      createdUnits: cu,
      unitsPerOrder: avgPerOrder(cu, co),
      despatchedOrders: dobs,
      despatchedUnits: dus,
      despatchedPct: co ? Math.round((dobs / co) * 1000) / 10 : 0,
    }
  }
  const outboundKpis = {
    b2c: channel(
      r => r.b2c_orders,
      r => r.b2c_units,
      r => r.b2c_despatched_orders,
      r => r.b2c_despatched_units,
    ),
    b2b: channel(
      r => r.b2b_orders,
      r => r.b2b_units,
      r => r.b2b_despatched_orders,
      r => r.b2b_despatched_units,
    ),
  }

  // ---- Outbound: charts (aligned to the canonical month axis) ----
  const row = m => obByM[m] || {}
  const ordersChart = months.map(m => {
    const r = row(m)
    return { created: (r.b2c_orders || 0) + (r.b2b_orders || 0), despatched: (r.b2c_despatched_orders || 0) + (r.b2b_despatched_orders || 0) }
  })
  const unitsChart = months.map(m => {
    const r = row(m)
    return { created: (r.b2c_units || 0) + (r.b2b_units || 0), despatched: (r.b2c_despatched_units || 0) + (r.b2b_despatched_units || 0) }
  })
  const mixChart = months.map(m => {
    const r = row(m)
    return { b2cOrders: r.b2c_orders || 0, b2bOrders: r.b2b_orders || 0, b2cUnits: r.b2c_units || 0, b2bUnits: r.b2b_units || 0 }
  })

  // ---- Outbound: detail table (created vs despatched totals + YoY on created) ----
  const outboundDetail = obWindow.map(r => {
    const isPartial = r.year_month === currentYM
    const created = (r.b2c_orders || 0) + (r.b2b_orders || 0)
    const despatched = (r.b2c_despatched_orders || 0) + (r.b2b_despatched_orders || 0)
    const createdUnits = (r.b2c_units || 0) + (r.b2b_units || 0)
    const prior = obByM[priorYm(r.year_month)]
    const priorCreated = prior ? (prior.b2c_orders || 0) + (prior.b2b_orders || 0) : 0
    return {
      label: fmtYM(r.year_month),
      partial: isPartial,
      created,
      despatched,
      uo: avgPerOrder(createdUnits, created),
      yoy: isPartial ? null : yoyPct(created, priorCreated),
    }
  })

  // ---- Inbound: KPIs, chart, table (closed receipts only) ----
  const inboundKpis = {
    lclInbound: { shipments: sum(ibComplete, r => r.lcl_shipments), units: sum(ibComplete, r => r.lcl_units) },
    fcl20: { shipments: sum(ibComplete, r => r.fcl_20_shipments), units: sum(ibComplete, r => r.fcl_20_units) },
    fcl40: { shipments: sum(ibComplete, r => r.fcl_40_shipments), units: sum(ibComplete, r => r.fcl_40_units) },
    returns: {
      units: sum(ibComplete, r => (r.returns_new_units || 0) + (r.returns_dmg_units || 0)),
      shipments: sum(ibComplete, r => (r.returns_new || 0) + (r.returns_dmg || 0)),
    },
  }
  const inboundByPeriod = months.map(m => ({
    lcl: ibByM[m]?.lcl_shipments || 0,
    fcl20: ibByM[m]?.fcl_20_shipments || 0,
    fcl40: ibByM[m]?.fcl_40_shipments || 0,
  }))
  const inboundReturnsDetail = ibWindow
    .filter(r => monthSet.has(r.year_month))
    .map(r => ({
      label: fmtYM(r.year_month),
      partial: r.year_month === currentYM,
      lcl: r.lcl_shipments,
      lclUnits: r.lcl_units || 0,
      fcl20: r.fcl_20_shipments,
      fcl20Units: r.fcl_20_units || 0,
      fcl40: r.fcl_40_shipments,
      fcl40Units: r.fcl_40_units || 0,
      returnsUnits: (r.returns_new_units || 0) + (r.returns_dmg_units || 0),
    }))

  return {
    name,
    companyCode: info?.company_code,
    period: { key: periodKey, label: periodLabel },
    labels,
    partialIndex,
    outboundKpis,
    ordersChart,
    unitsChart,
    mixChart,
    outboundDetail,
    inboundKpis,
    inboundByPeriod,
    inboundReturnsDetail,
    dwhUnavailable: !!dwh_unavailable,
  }
}
